import { observer, useLocalObservable } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import {
  EmptyTable,
  HelperText,
  ScopeTableRow,
  Table,
  TableHead,
  TableHeadCell,
} from '@ifs-gcp/components';
import type { IScopesNode } from '@ifs-gcp/core';
import type { PackagingPickerWidgetChild } from '@ifs-gcp/core/models';

import { useAuditStore } from '@/store/auditStore';
import { useTouchedField } from '@/store/validationStore';
import { getTranslation } from '@/utils/translations';

import { PackagingScopeRow } from './row';

interface WidgetPackagingScopesProps {
  element: IScopesNode<PackagingPickerWidgetChild>;
}

export const WidgetPackagingScopes = observer(({ element }: WidgetPackagingScopesProps) => {
  const {
    props: { id: widgetId, text, context, translations },
  } = element;

  const {
    i18n: { language },
  } = useTranslation();
  const audit = useAuditStore((state) => state.audit)!;
  const { isTouched, markTouched } = useTouchedField(widgetId, context);

  const domain = useLocalObservable(() => audit.factory('packagingScopes', element));

  const fieldError = isTouched ? audit.status.getErrorByTemplate(element) : null;

  const widgetError = fieldError?.errors.find(({ id }) => id === widgetId);

  return (
    <>
      {widgetError && (
        <HelperText error component="span">
          {getTranslation(widgetError.error, language)}
        </HelperText>
      )}

      <Table
        head={
          <TableHead>
            <TableHeadCell>{getTranslation(text, language)}</TableHeadCell>
          </TableHead>
        }
      >
        {element.props.scopes.length ? (
          element.props.scopes.map((scope) => (
            <ScopeTableRow key={scope.uuid}>
              <PackagingScopeRow
                language={language}
                domain={domain}
                template={scope}
                errors={fieldError?.errors}
                markTouched={markTouched}
              />
            </ScopeTableRow>
          ))
        ) : (
          <EmptyTable colSpan={1}>
            {getTranslation(translations.not_available, language)}
          </EmptyTable>
        )}
      </Table>
    </>
  );
});
